import { useCallback, useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { BuildType, DeploymentState } from 'common/CommonConstants';
import type { BuildSyncRequest } from 'common/core/BuildSyncDocuments';
import { ClientDeploymentDocument } from 'common/deployment/ClientDeploymentDocument';
import { validateDeployment } from 'common/deployment/validateDeployment';
import { EditAppContext, useEditApp } from 'client/lib/EditApp';
import { useSocket } from 'client/lib/Socket';
import { useApi } from 'client/lib/trpc';
import { type DeploymentClientState, useDeploymentClientState } from 'client/state/DeploymentClientState';
import { type OnLog } from 'client/state/LogState';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { PodInfoDialog } from 'client/components/Dialog/PodInfoDialog';
import { Dashboard } from 'client/components/Layout/Dashboard';
import { useLog } from 'client/components/Log/useLog';
import { DeploymentNotFound } from './DeploymentNotFound';
import { DeploymentOptionsTab } from './DeploymentOptionsTab';
import { DeploymentOverviewTab } from './DeploymentOverviewTab';
import { DeploymentRepositoryTab } from './DeploymentRepositoryTab';
import { DeploymentSubheader } from './DeploymentSubheader';

export const DeploymentPage = () => {
  const api = useApi();
  const params = useParams();
  const deploymentID = params.deploymentID ? parseInt(params.deploymentID) : 0;
  const projectID = params.projectID ? parseInt(params.projectID) : 0;
  const navigate = useNavigate();
  const { openLogDialog } = useLog();

  const [document, project] = useDeploymentClientState((state) => [state.document, state.project]);

  const deploymentStateID = useDeploymentStatusTree(deploymentID)?.deploymentStateID || DeploymentState.RUNNING;
  const containers = useDeploymentStatusTree(deploymentID)?.containers?.items;
  const [isNotFound, setIsNotFound] = useState(false);
  const isDeleted = [DeploymentState.DELETED, DeploymentState.DELETED_PENDING, DeploymentState.DELETED_ERROR].includes(deploymentStateID);

  const isNotFoundShown = isNotFound || isDeleted;

  useSocket({
    listeners: [
      /** Listener to update build info */
      {
        path: '/deployment/build',
        handler: (payload: BuildSyncRequest) => {
          const { project, document, builds } = useDeploymentClientState.getState();
          if (!document || !project || !builds) return;

          const { id: projectID } = project;
          const { id: deploymentID, stage } = document;

          // Pick out builds relevant to this project/deployment
          const relevantBuilds = payload.modifiedBuilds.filter(
            (b) =>
              (b.buildTypeID == BuildType.BASE_IMAGE && b.buildTargetID == projectID && b.stage == stage) ||
              (b.buildTypeID == BuildType.IMAGE && b.buildTargetID == deploymentID)
          );

          // Merge changes and sort
          for (const newBuild of relevantBuilds) {
            const index = builds.findIndex((b) => b.buildID == newBuild.buildID);
            if (index != -1) {
              builds[index] = newBuild;
            } else {
              builds.push(newBuild);
            }
          }
          builds.sort((a, b) => (a.buildID > b.buildID ? -1 : 1));

          useDeploymentClientState.setState({ builds });
        },
      },
    ],
  });

  const handleLogOpen = useCallback(
    async (params: Parameters<OnLog>[0]) => {
      const { type, level1, level2, level3 } = params;
      if (!project || !document) return;
      const { accountIdentifier, id: projectID, name: projectName } = project;
      const { id: deploymentID, name: deploymentName, stage: deploymentStage, componentsWithValues } = document;

      openLogDialog({
        type: type || '',
        level1: level1 || '',
        level2,
        level3,
        deployment: {
          accountIdentifier,
          projectID,
          projectName,
          deploymentID,
          deploymentName,
          deploymentStage,
          components: componentsWithValues.map((c) => ({
            componentName: c.name,
            componentUrl: c.packageUrl || '',
            namespace: c.identifier,
          })),
        },
      });
    },
    [projectID, deploymentID, containers, project, document, openLogDialog]
  );

  useEffect(() => {
    if (deploymentID === 0) navigate('../create');
  }, [deploymentID]);

  const handleError = useCallback((status: string, errorKey: string) => {
    if (errorKey === 'load' && status === 'deployment not found') {
      setIsNotFound(true);
      return false;
    }

    return true;
  }, []);

  const editAppContext = useEditApp<ClientDeploymentDocument, DeploymentClientState>({
    idKey: 'deploymentID',
    // @ts-expect-error useEditApp does not support tRPC properly
    loadUrl: api.deployment.load,
    // @ts-expect-error useEditApp does not support tRPC properly
    saveUrl: api.deployment.save,
    store: useDeploymentClientState,
    validator: validateDeployment,
    getLoadParams: useCallback(() => ({ projectID, cloneOfDeploymentID: 0 }), [projectID]),
    getSaveParams: (state) => {
      if (!state.document) return {};

      const document = {
        id: state.document.id,
        projectID: state.project?.id,
        name: state.document.name,
        stage: state.document.stage,
        coreInstanceTypeID: state.document.coreInstanceTypeID,
        cloneOfDeploymentID: state.document.cloneOfDeploymentID,
        allowEnableFromURL: state.document.allowEnableFromURL,
        gitConfigUser: state.document.gitConfigUser,
        componentsWithValues: state.document.componentsWithValues.map((component) => ({
          ...component,
          // Skip empty ENVs
          environmentVariables: component.environmentVariables.filter((env) => env.identifier.length > 0),
        })),
      };

      return { document };
    },
    isDocumentSaved: (current, previous) => {
      // We do not want the popup to trigger if enabling/disabling
      // coreInstanceTypeID is saved to DB when changing from dialog, no need to check here
      return isEqual(
        { ...(current || {}), deploymentStateID: DeploymentState.RUNNING, coreInstanceTypeID: 0 },
        { ...(previous || {}), deploymentStateID: DeploymentState.RUNNING, coreInstanceTypeID: 0 }
      );
    },
    onError: handleError,
  });

  return (
    <EditAppContext.Provider value={editAppContext}>
      <DeploymentSubheader />
      {isNotFoundShown && <DeploymentNotFound />}
      {!isNotFoundShown && <DeploymentDashboard onLog={handleLogOpen} />}
      <PodInfoDialog />
    </EditAppContext.Provider>
  );
};

const DeploymentDashboard = ({ onLog }: { onLog: OnLog }) => (
  <Dashboard>
    <Routes>
      <Route index element={<DeploymentOverviewTab onLog={onLog} />} />
      <Route path="repository" element={<DeploymentRepositoryTab />} />
      <Route path="options" element={<DeploymentOptionsTab />} />
    </Routes>
  </Dashboard>
);
